import React, { useEffect, useRef, useState } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { Modal } from "react-bootstrap";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { ROUTE_CONSTANT } from "../../../routes/constant";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { artistImageUrl } from "../../../utils/utils";
import JoditEditor from 'jodit-react';
import {
  FaEdit,
  FaArrowLeft,
  FaSearch,
  FaUsers,
  FaFileAlt,
  FaCheck,
  FaSave,
  FaEye,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import './EditArtistBio.css';

// Helper to decode URL-encoded artist names
const decodeArtistName = (name) => {
  if (!name) return 'Unknown Artist';
  try {
    return decodeURIComponent(name);
  } catch (e) {
    return name;
  }
};


const EditArtistBio = () => {
  const [artistBio, setArtistBio] = useState({
    artist_id: "",
    artist_name: "",
    artist_description: "",
    genres: "",
  });
  const [artists, setArtists] = useState([]);
  const [filteredArtists, setFilteredArtists] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editor = useRef(null);
  const artistIdFromUrl = searchParams.get('artist_id');

  // Jodit Editor Config
  const editorConfig = {
    readonly: false,
    placeholder: 'Start writing artist biography...',
    height: 350,
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'font', 'fontsize', 'paragraph', '|',
      'left', 'center', 'right', 'justify', '|',
      'link', 'image', '|',
      'undo', 'redo', '|',
      'hr', 'eraser', 'fullsize'
    ],
    toolbarAdaptive: false,
  };

  // Fetch all artists
  const fetchArtists = async () => {
    setLoading(true);
    try {
      let response = await allAPiServicesCall.getAllPlaylistBio(
        { page: 1, limit: 100 },
        getAuthConfig()
      );

      if (response.success && response.status === 200) {
        const artistsList = response.data.artistDetails || [];
        setArtists(artistsList);
        setFilteredArtists(artistsList);

        // Select artist from URL param or first artist by default
        if (artistsList.length > 0) {
          let selectedArtist = artistsList[0];

          // If artist_id is provided in URL, find and select that artist
          if (artistIdFromUrl) {
            const artistFromUrl = artistsList.find(a => String(a.artist_id) === String(artistIdFromUrl));
            if (artistFromUrl) {
              selectedArtist = artistFromUrl;
            }
          }

          setArtistBio({
            artist_id: selectedArtist.artist_id,
            artist_name: selectedArtist.artist_name,
            artist_description: selectedArtist.artist_description || "",
            genres: selectedArtist.genres || "",
          });
        }
      } else {
        console.warn("No data found");
      }
    } catch (error) {
      console.error("Error fetching artists:", error);
      toast.error("Failed to load artists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtists();
  }, [artistIdFromUrl]);

  // Filter artists based on search
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = artists.filter(artist =>
        artist.artist_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredArtists(filtered);
    } else {
      setFilteredArtists(artists);
    }
  }, [searchQuery, artists]);

  const handleArtistSelect = (artist) => {
    setArtistBio({
      artist_id: artist.artist_id,
      artist_name: artist.artist_name,
      artist_description: artist.artist_description || "",
      genres: artist.genres || "",
    });
  };

  const handleDescriptionChange = (content) => {
    setArtistBio((prevData) => ({
      ...prevData,
      artist_description: content,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!artistBio.artist_id) {
      toast.warning("Please select an artist first");
      return;
    }

    if (!artistBio.artist_description.trim()) {
      toast.warning("Please add a description for the artist");
      return;
    }

    setSaving(true);

    try {
      const response = await allAPiServicesCall.editArtistBio(
        {
          artist_id: artistBio.artist_id,
          artist_description: artistBio.artist_description,
        },
        getAuthConfig(),
        navigate
      );
      if (response.success) {
        toast.success("Artist bio updated successfully!");
        navigate(ROUTE_CONSTANT.COMMON.ARTIST_BIO);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update artist bio");
    } finally {
      setSaving(false);
    }
  };

  const getSelectedArtist = () => {
    return artists.find(a => a.artist_id === artistBio.artist_id);
  };

  return (
    <CommonLayout>
      <div className="edit-artist-bio-page">
        {/* Header Section */}
        <div className="edit-artist-bio-header">
          <div className="edit-artist-bio-header-content">
            <div className="edit-artist-bio-header-icon">
              <FaEdit />
            </div>
            <div className="edit-artist-bio-header-text">
              <h1 className="edit-artist-bio-title">Edit Artist Bio</h1>
              <p className="edit-artist-bio-subtitle">
                Update artist biographies and information
              </p>
            </div>
          </div>
          <button
            className="edit-artist-bio-back-btn"
            onClick={() => navigate(ROUTE_CONSTANT.COMMON.ARTIST_BIO)}
          >
            <FaArrowLeft /> Back to Artists
          </button>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="edit-artist-bio-loading">
            <div className="edit-artist-bio-loading-spinner"></div>
            <p className="edit-artist-bio-loading-text">Loading artists...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="edit-artist-bio-content">
              {/* Artist Selection Panel */}
              <div className="edit-artist-bio-selection-card">
                <div className="edit-artist-bio-selection-header">
                  <h3 className="edit-artist-bio-selection-title">
                    <FaUsers /> Select Artist
                  </h3>
                </div>
                <div className="edit-artist-bio-selection-body">
                  {/* Search */}
                  <div className="edit-artist-bio-search-wrapper">
                    <FaSearch className="edit-artist-bio-search-icon" />
                    <input
                      type="text"
                      className="edit-artist-bio-search-input"
                      placeholder="Search artists..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  {/* Artist List */}
                  <div className="edit-artist-bio-artist-list">
                    {filteredArtists.length > 0 ? (
                      filteredArtists.map((artist) => (
                        <div
                          key={artist.artist_id}
                          className={`edit-artist-bio-artist-item ${artistBio.artist_id === artist.artist_id ? 'selected' : ''}`}
                          onClick={() => handleArtistSelect(artist)}
                        >
                          {artistImageUrl(artist) && !artistImageUrl(artist).includes('default-image') ? (
                            <img
                              src={artistImageUrl(artist)}
                              alt={decodeArtistName(artist.artist_name)}
                              className="edit-artist-bio-artist-image"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div
                            className="edit-artist-bio-artist-placeholder"
                            style={{ display: artistImageUrl(artist) && !artistImageUrl(artist).includes('default-image') ? 'none' : 'flex' }}
                          >
                            <FaUser />
                          </div>
                          <div className="edit-artist-bio-artist-info">
                            <p className="edit-artist-bio-artist-name">{decodeArtistName(artist.artist_name)}</p>
                            <p className="edit-artist-bio-artist-genre">{artist.genres || 'No genre'}</p>
                          </div>
                          <div className="edit-artist-bio-artist-check">
                            <FaCheck />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="edit-artist-bio-no-results">
                        No artists found
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Editor Panel */}
              <div className="edit-artist-bio-editor-card">
                <div className="edit-artist-bio-editor-header">
                  <h3 className="edit-artist-bio-editor-title">
                    <FaFileAlt /> Biography Editor
                  </h3>
                  {artistBio.artist_name && (
                    <span className="edit-artist-bio-editor-badge">
                      Editing: {decodeArtistName(artistBio.artist_name)}
                    </span>
                  )}
                </div>
                <div className="edit-artist-bio-editor-body">
                  {/* Selected Artist Info */}
                  {getSelectedArtist() && (
                    <div className="edit-artist-bio-selected-artist">
                      {artistImageUrl(getSelectedArtist()) && !artistImageUrl(getSelectedArtist()).includes('default-image') ? (
                        <img
                          src={artistImageUrl(getSelectedArtist())}
                          alt={decodeArtistName(artistBio.artist_name)}
                          className="edit-artist-bio-selected-image"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div
                        className="edit-artist-bio-selected-placeholder"
                        style={{ display: artistImageUrl(getSelectedArtist()) && !artistImageUrl(getSelectedArtist()).includes('default-image') ? 'none' : 'flex' }}
                      >
                        <FaUser />
                      </div>
                      <div className="edit-artist-bio-selected-info">
                        <h4>{decodeArtistName(artistBio.artist_name)}</h4>
                        <p>Artist ID: {artistBio.artist_id} | Genre: {artistBio.genres || 'N/A'}</p>
                      </div>
                    </div>
                  )}

                  {/* Editor */}
                  <label className="edit-artist-bio-label">
                    <FaFileAlt /> Artist Description
                  </label>
                  <div className="edit-artist-bio-editor-container">
                    <JoditEditor
                      ref={editor}
                      value={artistBio.artist_description}
                      config={editorConfig}
                      onChange={handleDescriptionChange}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="edit-artist-bio-actions">
                    <button
                      type="button"
                      className="edit-artist-bio-btn edit-artist-bio-btn-cancel"
                      onClick={() => navigate(ROUTE_CONSTANT.COMMON.ARTIST_BIO)}
                    >
                      <FaTimes /> Cancel
                    </button>
                    <button
                      type="button"
                      className="edit-artist-bio-btn edit-artist-bio-btn-preview"
                      onClick={() => setShowPreview(true)}
                      disabled={!artistBio.artist_description}
                    >
                      <FaEye /> Preview
                    </button>
                    <button
                      type="submit"
                      className="edit-artist-bio-btn edit-artist-bio-btn-save"
                      disabled={saving || !artistBio.artist_id}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaSave /> Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
        centered
        className="edit-artist-bio-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEye /> Preview: {decodeArtistName(artistBio.artist_name)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="edit-artist-bio-preview-content">
            <h4>{decodeArtistName(artistBio.artist_name)}</h4>
            <div
              className="preview-text"
              dangerouslySetInnerHTML={{ __html: artistBio.artist_description }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="edit-artist-bio-btn edit-artist-bio-btn-cancel"
            onClick={() => setShowPreview(false)}
          >
            Close Preview
          </button>
        </Modal.Footer>
      </Modal>
    </CommonLayout>
  );
};

export default EditArtistBio;
